import {Injectable} from '@angular/core';
import {CameraService} from '../Scene/camera.service';
import {ContainerService} from '../../Utils/container.service';
import {Color, Face3, Geometry, Line3, Matrix4, Mesh, Plane, Quaternion, Vector2, Vector3} from 'three';
import {PolySizeService} from '../Calculation/poly-size.service';
import {ColorCodingService} from './color-coding.service';
import {EditorControlService} from '../../API/Public/editor-control.service';
import {CanvasEvent} from '../Control/controls';
import {PaintService} from './paint.service';

@Injectable({
    providedIn: 'root'
})
export class PolyPaintService {
    public indices = [];
    public readonly lassoControl = 'drawOnPoly';
    public readonly lassoTotalControl = 'drawAll';
    public readonly rectControl = 'drawOnPolyRect';

    constructor(private paintService: PaintService,
                private cameraService: CameraService,
                private containerService: ContainerService,
                private polySizeService: PolySizeService,
                private colorCodingService: ColorCodingService,
                private editorControlService: EditorControlService,
    ) {
        this.registerControls();
    }

    /**
     * initializes faces indices to be [0, 1, 2, ... , faces.length]
     */
    public initIndices() {
        this.indices = Array.from(Array((<Geometry>(<Mesh>this.containerService.renderGroup.children[0]).geometry).faces.length).keys());
    }

    /**
     * starts lasso Selection on Polygon
     */
    public lassoSelection(modelMesh: Mesh, selPoints2D: any, erase: boolean = false) {
        this.colorCodingService.initBufferPoly();
        const [x, y, w, h] = this.colorCodingService.getCheckarea(selPoints2D, 0);

        this.colorCodingService.renderColorpaintImg(x, y, w, h, true);
        for (let i = 0; i <= (w - x) * 4; i += 4) {
            for (let j = 0; j <= (h - y) * 4; j += 4) {
                const c = this.colorCodingService.checkVisibilityoverColors(i, j, w - x);
                if (c.r != null) {
                    const position: Vector2 = this.colorCodingService.transformToCenterCoord((x + (i / 4)), (y + (j / 4)));
                    if (this.pointInPolygon(position, selPoints2D)) {
                        const index = this.colorCodingService.getColorIndexPoly(c);
                        // this.markMeshTriangle(modelMesh, index, false);
                        if (erase) {
                            this.polySizeService.deleteMarkedID(index);
                        } else {
                            this.polySizeService.setMarkedID(index);
                        }
                    }
                }
            }
        }
    }

    /**
     * adds/removes faces to/from set of marked faces if brush stroke
     * has been drawn over them
     * @return {currentIndices: indices of selected faces after face deletion
     *          originalIndices: indices of selected faces before face deletion
     *          area: bounding box containing brush selection}
     */
    public brushSelection(modelMesh: Mesh, selPoints2D: any, brushsize: number, erase: boolean = false, roentgen = false): any {

        const currentIndexSet = new Set();
        const originalIndexSet = new Set();

        this.colorCodingService.initBufferPoly();
        const size = Number(brushsize.toFixed(2));

        // TODO: dynamic offset + check optimal scale factor instead of 15 (distance to camera)
        const offset = this.colorCodingService.resFactor * 15 * size;
        const checkArea = this.colorCodingService.getCheckarea(selPoints2D, offset);
        const [x, y, w, h] = checkArea;

        this.colorCodingService.renderColorpaintImg(x, y, w, h, true);
        this.colorCodingService.initBufferSelectionArea();
        this.colorCodingService.renderColorpaintImg(x, y, w, h, false);

        const relevantArea = [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
        for (let i = 0; i <= (w - x) * 4; i += 4) {
            for (let j = 0; j <= (h - y) * 4; j += 4) {
                this.checkIfMarked(i, j, w, x, currentIndexSet, originalIndexSet, checkArea, offset, relevantArea, roentgen, erase);
            }
        }

        return {
            currentIndices: (Array.from(currentIndexSet).sort((a: number, b: number) => b - a)),
            originalIndices: Array.from(originalIndexSet),
            area: relevantArea
        };

    }

    private checkIfMarked(i: number, j: number, w: number, x: number,
                          currentIndexSet: Set<any>, originalIndexSet: Set<any>,
                          checkArea: number[], offset: number, relevantArea: number[],
                          roentgen: boolean, erase: boolean) {
        const areaColor = this.colorCodingService.checkVisibilityoverColors(i, j, w - x);
        const brushArea = this.colorCodingService.opticalBrushSelection(i, j, w - x);

        if (areaColor != null && areaColor.r != null && brushArea.equals(this.paintService.paintColor.color)) {

            let index = this.colorCodingService.getColorIndexPoly(areaColor);
            currentIndexSet.add(index);

            if (roentgen) {
                this.updateRelevantArea(i, j, checkArea, relevantArea, offset);
                // get original index
                index = this.indices[index];
            }

            if (index !== undefined && index > -1) {
                originalIndexSet.add(index);

                // add/remove from set of marked faces
                if (erase) {
                    this.polySizeService.deleteMarkedID(index);
                } else {
                    this.polySizeService.setMarkedID(index);
                }

            }

        }
    }

    /**
     * Mark triangle
     * @param mesh: mesh to mark face in
     * @param faceID: index of face to mark
     * @param remove: if true removes color from face
     */
    private markMeshTriangle(mesh: Mesh, faceID, remove: boolean) {
        const geometry = (<Geometry> mesh.geometry);
        const face = geometry.faces[faceID];
        const color = remove ? new Color() : this.paintService.paintColor.color;

        face.vertexColors[0] = color;
        face.vertexColors[1] = color;
        face.vertexColors[2] = color;

        geometry.colorsNeedUpdate = true;
        geometry.verticesNeedUpdate = true;
        geometry.elementsNeedUpdate = true;
    }

    /**
     *
     * marks all Triangles, also not visible
     */
    public lassoSelectionTotal(modelMesh: Mesh, plane: Plane, selpoints: any): boolean {
        const selPlane = plane.clone();

        // compute rotation matrix only once per selection step
        const vecPlane = selPlane.normal.clone().normalize();
        const vecAxisZ = new Vector3(0, 0, -1);

        // rotate all points (the poly points and the face centers) such that the lie on a plane paralell to the x-y plane
        // this will allow to remove the z-cooard for further processing
        const quat = new Quaternion().setFromUnitVectors(vecPlane, vecAxisZ);
        const mat = new Matrix4();
        // mat.makeRotationY(-30*(Math.PI/180));
        mat.makeRotationFromQuaternion(quat);

        // rotate the selection polyogon points
        const selPoints = selpoints;
        const selPointsRot = [];
        const selPoints2D = [];

        for (let i = 0; i < selPoints.length; i++) {
            selPointsRot.push(selPoints[i].clone());
            selPointsRot[i].applyMatrix4(mat);

            selPoints2D.push(new Vector2(selPointsRot[i].x, selPointsRot[i].y));
        }

        const geometry = <Geometry>modelMesh.geometry;

        // go through all faces and project them onto the selection plane
        for (let i = 0; i < geometry.faces.length; i++) {
            const {a: vIdA, b: vIdB, c: vIdC} = geometry.faces[i];

            const ptA = geometry.vertices[vIdA].clone();
            const ptB = geometry.vertices[vIdB].clone();
            const ptC = geometry.vertices[vIdC].clone();

            // compute the face center point
            const center = (ptA.clone().add(ptB.clone().add(ptC)));
            center.divideScalar(3);
            const pointsHelp = [ptA.clone(), ptB.clone(), ptC.clone(), center.clone()];


            for (let ptc of pointsHelp) {
                // here we need a projection step that shows delivers the points in the actual coordinate system

                // correct for model transformation

                ptc = this.applyPointTrans(ptc);

                const vecPtCam = this.cameraService.camera.position.clone().sub(ptc);

                const lineStart = ptc.clone().sub(vecPtCam);
                const lineEnd = ptc.clone().add(vecPtCam.clone().multiplyScalar(2));
                const linePtCam = new Line3(lineStart, lineEnd);

                const iPoint = selPlane.intersectLine(linePtCam, new Vector3(0, 0, 0));

                // rotate such that the normal of the plane points to the z-direction
                if (iPoint) {
                    iPoint.applyMatrix4(mat);
                    const modelPoint2D = new Vector2(iPoint.x, iPoint.y);

                    // check if the modelPoint2D lies within selPoints2D
                    if (this.pointInPolygon(modelPoint2D, selPoints2D)) {

                        // const face = geometry.faces[i];
                        // this.markMeshTriangle(modelMesh, i, false);
                        // selFaceIDs.push(i);

                        this.polySizeService.setMarkedID(i);
                    }

                }
            }
        } // end for modelmesh.geometry.faces
        return false;
    } // end of function

    /**
     * mark the selected faces
     * (== faces in faceSet)
     * @param modelMesh
     */
    public applyLassoSelection(modelMesh: Mesh) {
        const selFaceIDs = Array.from(this.polySizeService.faceSet);
        for (let i = 0; i < selFaceIDs.length; i++) {
            this.markMeshTriangle(modelMesh, selFaceIDs[i], false);
        }

        // TODO: remove ?
        // this.polySizeService.calculateSurface(modelMesh);
        //  this.renderService.RenderLoop();
    }

    /**
     * get indices of regions that should not be marked
     * (== all regions but the one closest to the camera)
     * @param indices
     * @param modelMesh
     */
    public getIgnoredIndices(indices, modelMesh: Mesh): Array<number> {
        const regions = this.computeRegions(indices);
        const sortedRegions = Array.from(regions).sort((a, b) =>
            this.distanceToCamera(Array.from(a), <Geometry>modelMesh.geometry) -
            this.distanceToCamera(Array.from(b), <Geometry>modelMesh.geometry));

        const ignored = new Set<number>();

        // start at 1 as 0 is the relevant region
        for (let i = 1; i < sortedRegions.length; ++i) {
            const region = sortedRegions[i];
            region.forEach(index => {
                // get face index after deletion (roentgen)
                const newIndex = this.indices.indexOf(index);
                if (newIndex !== -1) {
                    ignored.add(newIndex);
                    this.polySizeService.deleteMarkedID(newIndex);
                }
            });
        }

        return Array.from(ignored);
    }

    /**
     * color faces with specific color
     * @param modelMesh: mesh of which faces should be colored
     * @param indices: indices of faces to color
     * @param color: color for faces
     */
    public colorFaces(modelMesh: Mesh, indices: Array<number>, color: Color) {
        for (let i = 0; i < indices.length; ++i) {
            const face = (<Geometry>modelMesh.geometry).faces[indices[i]];
            face.vertexColors = [color, color, color];

        }
        (<Geometry>modelMesh.geometry).colorsNeedUpdate = true;
        (<Geometry>modelMesh.geometry).verticesNeedUpdate = true;
        (<Geometry>modelMesh.geometry).elementsNeedUpdate = true;
    }

    /**
     * place a plane in the colorcoding selection to reduce artifacts
     * @param indices
     * @param modelMesh
     */
    public placeFilterPlane(indices: Array<number>, modelMesh: Mesh) {
        const geo = <Geometry>modelMesh.geometry;
        const sortedIndices = indices;
        sortedIndices.sort((a, b) => this.sortByCameraDistance(a, b, geo));
        const direction = new Vector3();
        this.cameraService.camera.getWorldDirection(direction);

        const mesh = this.containerService.helpPlane;
        const median = Math.floor(sortedIndices.length / 2);
        const v1 = geo.vertices[geo.faces[sortedIndices[median]].a].clone();
        v1.applyMatrix4(this.containerService.renderGroup.children[0].matrixWorld);

        mesh.quaternion.copy(this.cameraService.camera.quaternion);
        mesh.updateMatrix();
        const face = geo.faces[sortedIndices[median]];
        mesh.position.copy(v1.add(face.normal.negate().multiplyScalar(0.2)));
    }

    private sortByCameraDistance(a, b, geo) {
        return this.distanceToCameraFace(a, geo) > this.distanceToCameraFace(b, geo) ? 1 : -1;
    }

    /**
     * calculate face distance to camera
     * @param faceIndex
     * @param geo
     */
    private distanceToCameraFace(faceIndex: number, geo: Geometry) {
        return this.minVertexDistanceToCamera(geo.faces[faceIndex], geo);
    }

    /**
     * erase all marked areas from model
     */
    public resetDraw() {
        const mesh = <Mesh>this.containerService.renderGroup.children[0];
        const faceSet = Array.from(this.polySizeService.faceSet);
        for (const i of faceSet) {
            this.polySizeService.deleteMarkedID(Number(i));
            this.markMeshTriangle(mesh, Number(i), true);
        }
        this.polySizeService.resetParameters();
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private registerControls() {
        this.registerLassoControl(this.lassoControl);
        this.registerLassoControl(this.lassoTotalControl, true);

        // TODO: is polyRect still possible???
        // no more functionality in mouse service
        // this.editorControlService.registerControl(this.rectControl, {
        //     events: new Map([
        //         [CanvasEvent.Move, event => this.paintService.paint(event, false)],
        //         [CanvasEvent.Up, () => this.finishLassoPaint(total)]
        //     ])
        // });
    }

    private registerLassoControl(name, total = false) {
        this.editorControlService.registerControl(this.lassoControl, {
            events: new Map([
                [CanvasEvent.Down, event => { this.paintService.startPaintFromEvent(event); }],
                [CanvasEvent.Move, event => { this.paintService.paintForEvent(event, false); }],
                [CanvasEvent.Up, () => this.finishLassoPaint(total)]
            ])
        });
    }

    private finishLassoPaint(total = false) {
        this.paintService.closePoly();

        const model = <Mesh> this.containerService.renderGroup.children[0];
        this.paintService.drawToScene(true, false);
        if (total) {
            this.lassoSelectionTotal(model, this.paintService.plane, this.paintService._points);
        } else {
            this.lassoSelection(model, this.paintService.rawpoints);
        }
        this.applyLassoSelection(model);
        this.paintService.clearLassoPoints();
    }

    /**
     *
     * @param point
     * @param poly
     */
    private pointInPolygon(point: Vector2, poly) {
        const x = point.x;
        const y = point.y;

        let inside = false;
        for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
            const xi = poly[i].x, yi = poly[i].y;
            const xj = poly[j].x, yj = poly[j].y;

            const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) {
                inside = !inside;
            }
        }

        return inside;
    }

    private applyPointTrans(pt) {
        const p = pt.clone();
        // p.multiplyScalar(this.containerService.scalefactor);
        // p.y -= 150;
        p.z -= 15;

        return p;
    }

    private updateRelevantArea(i: number, j: number, checkArea, relevantArea, offset) {
        const currMinRawX = i / 4 + checkArea[4] - offset / 2;
        const currMinRawY = j / 4 + checkArea[5] - offset / 2;

        const currMaxRawX = i / 4 + checkArea[4] + offset / 2;
        const currMaxRawY = j / 4 + checkArea[5] + offset / 2;

        relevantArea[0] = Math.min(currMinRawX, relevantArea[0]);
        relevantArea[1] = Math.min(currMinRawY, relevantArea[1]);
        relevantArea[2] = Math.max(currMaxRawX, relevantArea[2]);
        relevantArea[3] = Math.max(currMaxRawY, relevantArea[3]);
    }

    /**
     * calculates all regions
     * @param indices
     */
    private computeRegions(indices) {
        const regions = new Array<Set<number>>();

        for (let i = 0; i < indices.length; ++i) {
            const index = indices[i];

            const includes = this.computeContainingRegions(regions, index);
            if (includes.length === 0) {
                // no regions contain index => create new region
                regions.push(new Set(this.containerService.faceNeighbours.get(index)));
            } else {
                // several regions contain index => merge to one region
                this.mergeContainingRegions(index, includes, regions);
            }

        }

        return regions;
    }

    private mergeContainingRegions(index, includes, regions) {
        const merged = new Set(this.containerService.faceNeighbours.get(index));
        for (let i = 0; i < includes.length; ++i) {
            const region = regions[includes[i]];
            region.forEach(j => merged.add(j));
        }

        for (let i = includes.length - 1; i >= 0; --i) {
            regions.splice(includes[i], 1);
        }
        regions.push(merged);
    }

    private computeContainingRegions(regions, index) {
        const includes = [];
        for (let i = 0; i < regions.length; ++i) {
            const region = regions[i];
            if (region.has(index)) {
                includes.push(i);
            }
        }
        return includes;
    }

    /**
     * calculates smallest distance of region's faces
     * @param region
     * @param geometry
     */
    private distanceToCamera(region: Array<number>, geometry: Geometry): number {
        let size = Number.MAX_SAFE_INTEGER;
        for (let i = 0; i < region.length; ++i) {
            const faceDist = this.minVertexDistanceToCamera(geometry.faces[this.indices.indexOf(region[i])], geometry);
            if (!isNaN(faceDist)) {
                size = Math.min(size, faceDist);
            }
        }

        return size;
    }

    /**
     * calculates min vertex distance of face
     * @param face
     * @param geometry
     */
    private minVertexDistanceToCamera(face: Face3, geometry: Geometry) {
        if (face !== undefined) {
            const v1 = geometry.vertices[face.a].clone();
            v1.applyMatrix4(this.containerService.renderGroup.children[0].matrixWorld);
            const v2 = geometry.vertices[face.b].clone();
            v2.applyMatrix4(this.containerService.renderGroup.children[0].matrixWorld);
            const v3 = geometry.vertices[face.c].clone();
            v3.applyMatrix4(this.containerService.renderGroup.children[0].matrixWorld);

            return Math.min(v1.distanceTo(this.cameraService.camera.position),
                            v2.distanceTo(this.cameraService.camera.position),
                            v3.distanceTo(this.cameraService.camera.position));
        }
    }
}
